import type { WebView } from '@/api';
import type { MaybeRefOrGetter, InjectionKey } from 'vue';

export const injectWebView = Symbol('__use_webview') as InjectionKey<{
    images: Readonly<Ref<Record<string, WebView['images'][number] | undefined>>>;
    contents: Readonly<Ref<Record<string, WebView['contents'][number] | undefined>>>;
}>;

export const useWebView = (webviewId: MaybeRefOrGetter<string>) => {
    const { state, error, isLoading } = useAsyncState(
        () =>
            api.getWebView({
                params: {
                    webview_id: toValue(webviewId),
                },
            }),
        undefined,
        {
            onError: e => toast.error('Failed to load page...'),
        },
    );

    const images = computed(
        (): Record<string, WebView['images'][number] | undefined> =>
            state.value?.data.images.reduce((accum, image) => ({ ...accum, [image.key]: image }), {}) ?? {},
    );

    const contents = computed(
        (): Record<string, WebView['contents'][number] | undefined> =>
            state.value?.data.contents.reduce(
                (accum, content) => ({
                    ...accum,
                    [content.key]: content,
                }),
                {},
            ) ?? {},
    );

    provide(injectWebView, {
        images,
        contents,
    });

    const image = (key: string): string | undefined => {
        const image = state.value?.data.images?.find(image => image.key === key);
        if (image == null) {
            return undefined;
        }
        return image.src;
    };

    const content = (key: string): string | undefined => {
        const content = state.value?.data.contents?.find(content => content.key === key);
        if (content == null) {
            return undefined;
        }
        return content.value;
    };

    return { images, contents, state, isLoading, image, content };
};
